/**
 * Rate-limiting throttle for API calls
 * Implements token bucket algorithm with concurrency control and retry logic
 */

const MAX_CONCURRENT = parseInt(import.meta.env.VITE_LLM_MAX_CONCURRENT || '5', 10);
const REQUESTS_PER_MINUTE = parseInt(import.meta.env.VITE_LLM_REQUESTS_PER_MINUTE || '20', 10);
const MIN_INTERVAL_MS = parseInt(import.meta.env.VITE_LLM_MIN_REQUEST_INTERVAL_MS || '3000', 10);

// Retry configuration
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY_MS = 5000;

class RateLimiter {
  constructor(maxConcurrent, requestsPerMinute, minIntervalMs) {
    this.maxConcurrent = maxConcurrent;
    this.requestsPerMinute = requestsPerMinute;
    this.minIntervalMs = minIntervalMs;

    this.activeRequests = 0;
    this.requestTimes = [];
    this.queue = [];
    this.lastRequestTime = 0;
  }

  async acquire() {
    return new Promise((resolve) => {
      this.queue.push(resolve);
      this.processQueue();
    });
  }

  release() {
    this.activeRequests--;
    this.processQueue();
  }

  processQueue() {
    if (this.queue.length === 0) return;

    // Check concurrency limit
    if (this.activeRequests >= this.maxConcurrent) return;

    // Check minimum interval
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    if (timeSinceLastRequest < this.minIntervalMs) {
      setTimeout(() => this.processQueue(), this.minIntervalMs - timeSinceLastRequest);
      return;
    }

    // Check requests per minute limit (sliding window)
    const oneMinuteAgo = now - 60000;
    this.requestTimes = this.requestTimes.filter(t => t > oneMinuteAgo);

    if (this.requestTimes.length >= this.requestsPerMinute) {
      // Wait until oldest request falls out of the window
      const waitTime = this.requestTimes[0] - oneMinuteAgo + 100;
      setTimeout(() => this.processQueue(), waitTime);
      return;
    }

    // All checks passed, allow the request
    const resolve = this.queue.shift();
    this.activeRequests++;
    this.requestTimes.push(now);
    this.lastRequestTime = now;
    resolve();
  }

  /**
   * Pause all requests for a specified duration (used when hitting rate limits)
   */
  async pauseFor(ms) {
    console.log(`[RateLimiter] Pausing for ${Math.round(ms / 1000)}s due to rate limit`);
    await new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Execute a function with rate limiting and retry on 429 errors
   * @param {function} fn - Async function to execute
   * @param {number} retryCount - Current retry count (internal use)
   * @returns {Promise} - Result of the function
   */
  async execute(fn, retryCount = 0) {
    await this.acquire();
    let didRetry = false;
    try {
      return await fn();
    } catch (error) {
      // Check if it's a rate limit error (429)
      if (error.message && error.message.includes('429') && retryCount < MAX_RETRIES) {
        // Extract retry delay from error message if available
        let retryDelay = INITIAL_RETRY_DELAY_MS * Math.pow(2, retryCount);

        const retryMatch = error.message.match(/retry in (\d+(?:\.\d+)?)/i);
        if (retryMatch) {
          retryDelay = Math.ceil(parseFloat(retryMatch[1]) * 1000) + 1000; // Add 1s buffer
        }

        console.log(`[RateLimiter] Rate limited, retry ${retryCount + 1}/${MAX_RETRIES} in ${Math.round(retryDelay / 1000)}s`);

        // Mark that we're retrying so we don't double-release
        didRetry = true;

        // Release the slot before waiting
        this.release();

        // Wait before retrying
        await this.pauseFor(retryDelay);

        // Retry with incremented count (acquire will be called again)
        return this.execute(fn, retryCount + 1);
      }
      throw error;
    } finally {
      // Only release if we didn't already release for retry
      if (!didRetry) {
        this.release();
      }
    }
  }

  /**
   * Get current status for debugging
   */
  getStatus() {
    return {
      activeRequests: this.activeRequests,
      queueLength: this.queue.length,
      requestsInLastMinute: this.requestTimes.length,
      maxConcurrent: this.maxConcurrent,
      requestsPerMinute: this.requestsPerMinute
    };
  }
}

// Singleton instance for LLM requests
export const llmRateLimiter = new RateLimiter(MAX_CONCURRENT, REQUESTS_PER_MINUTE, MIN_INTERVAL_MS);

/**
 * Wrap an async function with rate limiting
 * @param {function} fn - Async function to wrap
 * @returns {function} - Rate-limited version of the function
 */
export function withRateLimit(fn) {
  return async (...args) => {
    return llmRateLimiter.execute(() => fn(...args));
  };
}
